import { useState } from "react";
import { Check, Loader2, Sparkles, Undo2 } from "lucide-react";

import { describeDrawing } from "@/api/client";
import type { ProposedFact } from "@/api/schemas";
import { useIntakeStore, type ConfirmedNacrtFact } from "./useIntakeStore";

/**
 * B1 — vision-over-nacrti (mandate 2026-07-28). A COMPLEMENT to the
 * deterministic DXF/IFC extraction: the architect reads an uploaded drawing
 * with the AI ("kao da si ga ubacio u Claude") and gets a qualitative
 * description + PROPOSED facts. Nothing is grounded until the architect
 * confirms it — propose-then-confirm, the same bar as every amber value.
 *
 * Rendered inside a ④ Nacrti row, once per uploaded drawing that carries a
 * ``doc_id``. The description + confirmed facts travel with the generate
 * request (``nacrt_opis`` / ``nacrt_confirmed_facts``); the generated "Opis
 * prema nacrtima" section is badged "generirano — pregledaj".
 */
export function NacrtVisionPanel({
  projectId,
  docId,
  docType,
}: {
  projectId: string;
  docId: string;
  docType: string;
}) {
  const setVision = useIntakeStore((s) => s.setNacrtVision);
  const result = useIntakeStore((s) =>
    s.get(projectId).nacrtVision.find(
      (r) => String(r.provenance?.doc_id ?? "") === docId,
    ),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await describeDrawing(docId, docType);
      if (res.skipped) {
        setError("Ovaj tip dokumenta nije nacrt — AI čitanje se preskače.");
        return;
      }
      if (res.error && !res.opis_prose && res.predlozeni_fakti.length === 0) {
        setError("AI čitanje trenutno nije dostupno. Pokušaj ponovno.");
        return;
      }
      setVision(projectId, res);
    } catch {
      setError("AI čitanje nije uspjelo. Pokušaj ponovno.");
    } finally {
      setBusy(false);
    }
  };

  if (!result) {
    return (
      <div className="mx-2 my-1">
        <button
          type="button"
          disabled={busy}
          onClick={() => void run()}
          className="inline-flex items-center gap-1.5 rounded border border-ind-300 bg-white px-2.5 py-1 text-[0.76rem] font-semibold text-ind-700 hover:bg-ind-50 disabled:opacity-60"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          {busy ? "AI čita nacrt…" : "Pročitaj nacrt (AI)"}
        </button>
        {error && (
          <p className="mt-1 text-[0.72rem] text-amber-700">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className="mx-2 my-1 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2">
      <div className="mb-1 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-amber-600" />
        <span className="text-[0.72rem] font-bold uppercase tracking-[0.04em] text-amber-800">
          AI opis nacrta — generirano, pregledaj
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() => void run()}
          className="ml-auto text-[0.7rem] font-semibold text-ind-700 hover:text-ind-800 disabled:opacity-60"
        >
          {busy ? "…" : "ponovno"}
        </button>
      </div>

      {result.opis_prose && (
        <p className="whitespace-pre-line text-[0.78rem] leading-relaxed text-stone-700">
          {result.opis_prose}
        </p>
      )}

      {result.predlozeni_fakti.length > 0 && (
        <div className="mt-2">
          <p className="mb-1 text-[0.7rem] text-stone-500">
            Predložene značajke — potvrdi one koje su točne (ulaze u opis kao
            provjerena činjenica):
          </p>
          <ul className="flex flex-col gap-1">
            {result.predlozeni_fakti.map((f) => (
              <ProposedFactRow
                key={`${f.kljuc}-${f.vrijednost}`}
                projectId={projectId}
                docType={result.doc_type}
                fact={f}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ProposedFactRow({
  projectId,
  docType,
  fact,
}: {
  projectId: string;
  docType: string;
  fact: ProposedFact;
}) {
  const confirm = useIntakeStore((s) => s.confirmNacrtFact);
  const unconfirm = useIntakeStore((s) => s.unconfirmNacrtFact);
  const confirmed = useIntakeStore((s) =>
    s.isNacrtFactConfirmed(projectId, docType, fact.kljuc, fact.vrijednost),
  );

  const asConfirmed: ConfirmedNacrtFact = {
    kljuc: fact.kljuc,
    vrijednost: fact.vrijednost,
    tip: fact.tip,
    doc_type: docType,
    citat_vizualni: fact.citat_vizualni,
  };

  return (
    <li className="flex items-start gap-2 rounded border border-amber-200/70 bg-white px-2 py-1">
      <div className="min-w-0 flex-1">
        <span className="text-[0.78rem] text-stone-800">
          <span className="font-semibold">{fact.vrijednost}</span>
          {fact.tip === "brojcano" && (
            <span className="ml-1 rounded bg-amber-100 px-1 text-[0.66rem] font-semibold text-amber-800">
              brojčano — provjeri pažljivo
            </span>
          )}
        </span>
        {fact.citat_vizualni && (
          <span className="mt-0.5 block text-[0.68rem] text-stone-400">
            {fact.citat_vizualni}
            {fact.low_trust && " · niska sigurnost"}
          </span>
        )}
      </div>
      {confirmed ? (
        <button
          type="button"
          onClick={() =>
            unconfirm(projectId, docType, fact.kljuc, fact.vrijednost)
          }
          className="inline-flex shrink-0 items-center gap-1 rounded border border-green-300 bg-green-50 px-2 py-0.5 text-[0.72rem] font-semibold text-green-800 hover:bg-green-100"
        >
          <Check className="h-3 w-3" /> potvrđeno
          <Undo2 className="h-3 w-3 opacity-60" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => confirm(projectId, asConfirmed)}
          className="inline-flex shrink-0 items-center gap-1 rounded border border-amber-300 bg-white px-2 py-0.5 text-[0.72rem] font-semibold text-amber-900 hover:bg-amber-100"
        >
          <Check className="h-3 w-3" /> Potvrdi
        </button>
      )}
    </li>
  );
}
